import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema } from "@/lib/jsonld";

const Legal = () => {
  const jsonLdSchemas = [
    generateWebPageSchema("Conditions générales", seoConfig.legal.description, "/legal"),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Conditions générales", path: "/legal" }
    ])
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.legal} jsonLd={jsonLdSchemas} />
      <Header />
      <main className="flex-1">
        {/* Hero Section - Titre principal */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] text-center">
              Conditions générales d'utilisation et de vente
            </h1>
          </div>
        </section>

        {/* Contenu des articles */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Article 1 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
              Article 1 : Champ d'application
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les présentes conditions générales d'utilisation et de vente (ci-après les « Conditions ») régissent l'ensemble des relations contractuelles entre Unotif, marque exploitée par la société SEO FRENCH TOUCH LLC, et toute personne physique ou morale utilisant les services proposés via le site internet, l'application web ou tout autre support associé à Unotif.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les présentes Conditions s'appliquent à toute utilisation, toute souscription, toute commande et tout accès aux services Unotif, sans restriction ni réserve.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Est considérée comme « Client » toute personne physique ou morale :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>ayant créé un compte utilisateur sur Unotif,</li>
              <li>et/ou ayant souscrit à un abonnement gratuit ou payant,</li>
              <li>et/ou ayant effectué un paiement validé via notre partenaire de paiement Stripe.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'utilisation des services Unotif implique l'acceptation pleine, entière et sans réserve des présentes Conditions.
            </p>

            {/* Article 2 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 2 : Description du service
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif est une solution SaaS (Software as a Service) permettant aux utilisateurs de créer, gérer et diffuser des notifications numériques associées à des cartes digitales compatibles notamment avec Apple Wallet et Google Wallet.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les fonctionnalités proposées par Unotif varient selon le plan d'abonnement souscrit (gratuit, Starter, Pro, ou tout autre plan proposé ultérieurement).
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif se réserve le droit de faire évoluer, modifier, suspendre ou enrichir ses fonctionnalités à tout moment, dans l'intérêt du service et de ses utilisateurs.
            </p>

            {/* Article 3 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 3 : Commande et création de compte
            </h2>
            
            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              3.1 Création de compte
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'accès aux services Unotif nécessite la création préalable d'un compte utilisateur.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le Client s'engage à fournir des informations exactes, complètes et à jour.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif se réserve le droit de refuser, suspendre ou supprimer tout compte comportant des informations inexactes, trompeuses ou frauduleuses.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              3.2 Validation de commande
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Toute commande ou souscription effectuée sur le site Unotif implique l'acceptation intégrale des présentes Conditions.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La validation du paiement vaut conclusion définitive du contrat entre Unotif et le Client.
            </p>

            {/* Article 4 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 4 : Prix et politique tarifaire
            </h2>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              4.1 Prix
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les prix des abonnements sont indiqués en euros (€) et hors taxes (HT), sauf mention contraire.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif se réserve le droit de modifier ses tarifs à tout moment.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Toutefois, le tarif appliqué au Client est celui en vigueur au moment de la souscription ou du renouvellement de l'abonnement.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              4.2 Abonnements non remboursables
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les abonnements Unotif, qu'ils soient mensuels ou annuels, sont fermement et définitivement non remboursables, y compris en cas d'utilisation partielle, d'erreur de souscription ou de résiliation anticipée.
            </p>

            {/* Article 5 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 5 : Modalités de paiement
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le paiement est exigible immédiatement à la date de la commande ou du renouvellement automatique.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les paiements sont effectués exclusivement par carte bancaire via notre prestataire de paiement sécurisé Stripe.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Sont acceptées uniquement les cartes bancaires internationales Visa et Mastercard.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les cartes American Express ne sont pas acceptées.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les informations bancaires transmises sont chiffrées et sécurisées selon les standards en vigueur.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif n'a à aucun moment accès aux données bancaires du Client.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Toute garantie relative à la sécurité du système de paiement relève exclusivement de la responsabilité de la société Stripe.
            </p>

            {/* Article 6 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 6 : Durée, renouvellement et résiliation
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les abonnements Unotif sont souscrits pour une durée mensuelle ou annuelle, selon le choix du Client.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Sauf résiliation explicite avant la date d'échéance, l'abonnement est renouvelé automatiquement pour une période identique.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le Client peut résilier son abonnement à tout moment depuis son espace utilisateur.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La résiliation prend effet à la fin de la période de facturation en cours.
            </p>

            {/* Article 7 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 7 : Limites d'utilisation et quotas
            </h2>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              7.1 Principe général
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les services Unotif sont soumis à des limitations d'usage définies par plan d'abonnement.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces limitations ont pour objectif de garantir la stabilité, la sécurité et l'équité du service pour l'ensemble des utilisateurs.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les quotas sont mensuels, non cumulables, non reportables d'un mois à l'autre.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              7.2 Notifications : Quotas mensuels
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Afin de simplifier la gestion des mois comportant 28, 30 ou 31 jours, Unotif applique un système de quotas mensuels fixes.
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li><span className="font-semibold">Plan Gratuit</span> : jusqu'à 4 diffusions par mois</li>
              <li><span className="font-semibold">Plan Starter</span> : jusqu'à 100 diffusions par mois</li>
              <li><span className="font-semibold">Plan Pro</span> : jusqu'à 1 000 diffusions par mois</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces quotas tiennent compte du fait que, techniquement, chaque carte est déjà limitée à 3 notifications par fenêtre glissante de 24 heures.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En utilisant Unotif, le Client reconnaît et accepte expressément ces limitations.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <span className="font-semibold">Définition d'une diffusion</span> : Une diffusion représente une campagne de notification push envoyée à vos abonnés, quel que soit le nombre d'abonnés qui la reçoivent. Ainsi, envoyer une notification à 10, 1 000 ou l'ensemble de vos abonnés compte pour 1 seule diffusion.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <span className="font-semibold">Notifications géolocalisées</span> : Les notifications géolocalisées ne consomment aucune diffusion. Elles fonctionnent de manière automatique et illimitée une fois configurées.
            </p>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Dépassement de quota
            </h4>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En cas d'atteinte du quota mensuel :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>l'envoi de nouvelles notifications est automatiquement bloqué,</li>
              <li>le blocage est levé automatiquement lors de la réinitialisation mensuelle du quota,</li>
              <li>aucun dépassement, contournement ou empilement de quota n'est autorisé.</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              7.3 Réinitialisation des quotas
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les quotas sont réinitialisés automatiquement :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>pour les <span className="font-semibold">comptes gratuits</span> : à la date anniversaire de création du compte chaque mois,</li>
              <li>pour les <span className="font-semibold">comptes payants</span> (Starter et Pro) : à la date anniversaire de souscription de l'abonnement.</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              7.4 Cartes et installations
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <span className="font-semibold">Définition d'une installation</span> : Une installation correspond à un utilisateur qui ajoute votre carte Wallet à son appareil (Apple Wallet sur iPhone ou Google Wallet sur Android). Une personne ne peut installer une carte qu'une seule fois par appareil. Si un utilisateur supprime puis réinstalle votre carte, cela compte comme une nouvelle installation.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <span className="font-semibold">Définition d'une carte</span> : Une carte est un pass Wallet créé sur Unotif avec son propre design, contenu, notifications et page de partage.
            </p>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Plan Gratuit
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>1 carte maximum</li>
              <li>Jusqu'à 100 installations (Apple & Google Wallet)</li>
              <li>En cas de suppression de cette carte, le Client ne pourra pas en créer une nouvelle, sauf passage à un plan payant.</li>
            </ul>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Plan Starter
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>1 carte maximum</li>
              <li>Jusqu'à 2 500 installations (Apple & Google Wallet)</li>
              <li>Suppression possible d'une carte avec possibilité d'en recréer une nouvelle dans la limite du plan.</li>
            </ul>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Plan Pro
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Jusqu'à 10 cartes actives</li>
              <li>Jusqu'à 25 000 installations cumulées (Apple & Google Wallet)</li>
            </ul>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Gestion des installations
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Les installations ne se réinitialisent pas mensuellement</li>
              <li>C'est un quota permanent tant que les utilisateurs gardent votre carte installée</li>
              <li>Si un utilisateur supprime votre carte, cette installation est libérée et peut être utilisée par un nouvel utilisateur</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En cas de dépassement des limites d'installations :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>les boutons d'installation sont automatiquement désactivés,</li>
              <li>ils sont réactivés automatiquement dès que la limite redevient conforme.</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              7.5 Autres limitations fonctionnelles
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Selon le plan souscrit, certaines limitations supplémentaires peuvent s'appliquer, notamment :
            </p>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-2">
              <span className="font-semibold">Code d'intégration (embed)</span> :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Non disponible sur le plan Gratuit</li>
              <li>Disponible sur les plans Starter et Pro</li>
            </ul>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-2">
              <span className="font-semibold">Notifications géolocalisées</span> :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Non disponibles sur le plan Gratuit</li>
              <li>Disponibles sur le plan Starter</li>
              <li>Disponibles en version avancée sur le plan Pro</li>
            </ul>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-2">
              <span className="font-semibold">Tags et segmentation</span> :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Non disponibles sur le plan Gratuit</li>
              <li>Nombre de tags par carte : maximum 10 tags (plans Starter et Pro)</li>
              <li>Disponibles sur les plans Starter et Pro</li>
            </ul>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-2">
              <span className="font-semibold">Statistiques et analyses</span> :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Gestion basique sur le plan Gratuit</li>
              <li>Statistiques et analyses détaillées sur les plans Starter et Pro</li>
            </ul>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-2">
              <span className="font-semibold">Retrait de la marque Unotif</span> :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Non disponible sur le plan Gratuit</li>
              <li>Disponible sur les plans Starter et Pro</li>
            </ul>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <span className="font-semibold">Liens au verso de la carte</span> : 2 liens standards, avec possibilité de liens supplémentaires spécifiques à Google Wallet via les modules Google (selon plan souscrit)
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <span className="font-semibold">Accès à certaines fonctionnalités avancées</span> : analytics, modules spécifiques, segmentation avancée (selon plan souscrit)
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              7.6 Conséquences en cas de dépassement des limites
            </h3>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Dépassement des installations
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Les nouvelles installations sont automatiquement bloquées</li>
              <li>Les utilisateurs existants conservent leur carte</li>
              <li>Passage à un plan supérieur nécessaire pour accepter de nouvelles installations</li>
            </ul>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Dépassement des diffusions
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Envoi de nouvelles notifications bloqué jusqu'à la réinitialisation mensuelle</li>
              <li>Les notifications géolocalisées continuent de fonctionner normalement</li>
              <li>Possibilité d'attendre la réinitialisation mensuelle ou de passer à un plan supérieur</li>
            </ul>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Changement de plan avec dépassement
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>En cas de passage à un plan inférieur alors que vos cartes dépassent les nouvelles limites (nombre de cartes ou d'installations), ces cartes seront supprimées instantanément</li>
              <li>La suppression de cartes entraîne la perte immédiate de tous les abonnés associés</li>
            </ul>

            <h4 className="text-base sm:text-lg font-semibold text-[#022226] mb-2 mt-6">
              Résiliation avec dépassement
            </h4>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>Après résiliation, si vos cartes dépassent les limites du plan Gratuit, elles seront automatiquement supprimées</li>
              <li>La suppression entraîne la perte immédiate de tous les abonnés associés</li>
              <li>Impossibilité d'envoyer des notifications ou de modifier vos cartes tant que les limites ne sont pas respectées</li>
            </ul>

            {/* Article 8 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 8 : Protection des données personnelles
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Conformément à la législation en vigueur, notamment la Loi Informatique et Libertés et le RGPD, le Client dispose d'un droit d'accès, de rectification et de suppression de ses données personnelles.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En utilisant Unotif, le Client reconnaît avoir pris connaissance de la politique de protection des données personnelles et consent à la collecte et à l'utilisation de ses données dans le cadre du service.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le Client peut recevoir des emails d'information ou de communication relatifs à Unotif.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Il peut se désinscrire à tout moment via le lien de désinscription figurant en bas de chaque email, précédé de la mention « Se désinscrire ».
            </p>

            {/* Article 9 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 9 : Accord d'usage non abusif
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif applique un accord d'usage non abusif visant à garantir une utilisation raisonnable, équitable et durable du service.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Toute utilisation susceptible :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>de nuire à la qualité du service pour les autres utilisateurs,</li>
              <li>de générer des coûts disproportionnés,</li>
              <li>ou de rendre le service non viable économiquement,</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              pourra entraîner des mesures correctives.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Dans les cas extrêmes ou urgents, Unotif se réserve le droit de :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>restreindre,</li>
              <li>suspendre,</li>
              <li>ou bloquer l'accès aux services,</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              après ou concomitamment à une prise de contact avec le Client afin d'évaluer les solutions possibles.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les clients prévoyant des volumes d'audience importants sont invités à contacter le support Unotif en amont.
            </p>

            {/* Article 10 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 10 : Contenu interdit
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La société SEO FRENCH TOUCH LLC, exploitant la marque Unotif, interdit strictement toute utilisation du service liée notamment à :
            </p>
            <ul className="list-disc list-inside space-y-2 mb-4 text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
              <li>la pornographie,</li>
              <li>les jeux d'argent,</li>
              <li>les produits ou services illégaux,</li>
              <li>la violence,</li>
              <li>la fraude,</li>
              <li>l'exploitation ou toute activité contraire à la loi ou à l'ordre public.</li>
            </ul>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Tout compte enfreignant ces règles pourra être suspendu ou supprimé sans préavis, sans possibilité de remboursement.
            </p>

            {/* Article 11 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 11 : Responsabilité
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif ne saurait être tenue responsable des dommages indirects, pertes de données, pertes financières ou préjudices commerciaux résultant de l'utilisation ou de l'impossibilité d'utiliser le service.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le Client demeure seul responsable du contenu qu'il diffuse via Unotif.
            </p>

            {/* Article 12 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 12 : Droit applicable et juridiction compétente
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les présentes Conditions sont régies par le droit applicable aux sociétés de droit américain.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Tout litige relatif à l'interprétation ou à l'exécution des présentes Conditions relève de la compétence exclusive des juridictions compétentes.
            </p>

            {/* Article 13 */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Article 13 : Informations sur la société
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              SEO FRENCH TOUCH LLC
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              539 W. COMMERCE ST #6642
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              DALLAS, TX 75208
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              États-Unis
            </p>

          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Legal;
